/**
 * Grammar + conversions for Spotlight "mode paths" — URL-like hierarchical mode
 * identifiers such as `/`, `/settings`, `/settings/theme`.
 *
 * Rules:
 * - A path MUST start with `/`. The root is exactly `/`.
 * - A non-root path is `/seg` or `/seg/seg/…`; each segment matches
 *   `[a-zA-Z_-][0-9a-zA-Z_-]*`.
 * - No trailing slash except the root `/`.
 *
 * Invalid input throws — every Spotlight boundary that accepts a path validates
 * here first, so a malformed path never reaches the store.
 */

/** One path segment (no leading/trailing slash). */
export const SEGMENT_PATTERN = /^[a-zA-Z_-][0-9a-zA-Z_-]*$/;

/** A full mode path: the root `/`, or one or more `/segment` groups, no trailing slash. */
export const MODE_PATH_PATTERN = /^\/$|^(\/[a-zA-Z_-][0-9a-zA-Z_-]*)+$/;

/** A path may be given as its string form or as an array of segments (`[]` ⇒ root). */
export type TModePathInput = string | string[];

/** Whether `value` is a well-formed mode path string. */
export function isValidModePath(value: string): boolean {
  return MODE_PATH_PATTERN.test(value);
}

/** Return `value` if it is a valid mode path, else throw. */
export function assertModePath(value: string): string {
  if (!isValidModePath(value)) {
    throw new Error(
      `Invalid mode path "${value}": must start with "/", each segment matching [a-zA-Z_-][0-9a-zA-Z_-]* (no leading digit), and have no trailing slash (root is "/").`
    );
  }
  return value;
}

/** Throw if any element is not a valid path segment. */
function assertSegments(segments: string[]): string[] {
  for (const segment of segments) {
    if (!SEGMENT_PATTERN.test(segment)) {
      throw new Error(
        `Invalid mode path segment "${segment}": must match [a-zA-Z_-][0-9a-zA-Z_-]*.`
      );
    }
  }
  return segments;
}

/** Normalize a path/segments input to a validated segment array (`/` ⇒ `[]`). */
export function toSegments(input: TModePathInput): string[] {
  if (Array.isArray(input)) return assertSegments([...input]);
  assertModePath(input);
  return input === '/' ? [] : input.slice(1).split('/');
}

/** Normalize a path/segments input to a validated path string (`[]` ⇒ `/`). */
export function toPath(input: TModePathInput): string {
  if (!Array.isArray(input)) return assertModePath(input);
  const segments = assertSegments([...input]);
  return segments.length === 0 ? '/' : `/${segments.join('/')}`;
}

/**
 * All prefixes of `path`, root → exact, e.g.
 * `/settings/theme` → `['/', '/settings', '/settings/theme']`. Root → `['/']`.
 */
export function prefixesOf(path: string): string[] {
  const segments = toSegments(path);
  const prefixes = ['/'];
  let acc = '';
  for (const segment of segments) {
    acc += `/${segment}`;
    prefixes.push(acc);
  }
  return prefixes;
}
